import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Cpu, Droplets, Activity, Bot, Satellite, Leaf, Layers,
  Wheat, TrendingUp, Trees, FileText, Languages, Moon, Sun, CheckCircle
} from 'lucide-react';
import GeoLogo from '../brand/Logo_geo.svg';
import Toast from '../components/ui/Toast';
import OnboardingWizard from '../components/OnboardingWizard';

/* ─── Feature data ─────────────────────────────────────────────────────────── */
const SHOWCASE_FEATURES = [
  { id: 'iot', Icon: Cpu, color: '#6B8F71', img: '/assets/features/iot-device.webp', en: ['IoT Live Sensors', 'Real-time farm device readings'], ar: ['حساسات IoT الحية', 'قراءات حية لأجهزة المزرعة'] },
  { id: 'irr', Icon: Droplets, color: '#4A90D9', img: '/assets/features/irrigation.webp', en: ['Smart Irrigation', 'Automated irrigation & fertilization'], ar: ['الري الذكي', 'ري وتسميد تلقائي'] },
  { id: 'diag', Icon: Activity, color: '#C47A2C', img: '/assets/features/ai-diagnosis.webp', en: ['AI General Diagnosis', 'AI analysis of sensor readings'], ar: ['تشخيص الذكاء العام', 'تحليل ذكي لبيانات الحساسات'] },
  { id: 'consult', Icon: Bot, color: '#8B5CF6', img: '/assets/features/consultant.webp', en: ['AI Ag Consultant', 'Expert agri recommendations by AI'], ar: ['المستشار الزراعي', 'توصيات زراعية من الذكاء الاصطناعي'] },
  { id: 'sat', Icon: Satellite, color: '#0EA5E9', img: '/assets/features/satellite.webp', en: ['Satellite Farm Analysis', 'NDVI & crop indices from space'], ar: ['تحليل فضائي للمزرعة', 'مؤشرات NDVI من الأقمار الصناعية'] },
  { id: 'leaf', Icon: Leaf, color: '#6B8F71', img: '/assets/features/leaf-doctor.webp', en: ['Leaf Doctor', 'AI plant disease detection'], ar: ['طبيب النبات', 'كشف أمراض النبات بالذكاء الاصطناعي'] },
  { id: 'soil', Icon: Layers, color: '#92664D', img: '/assets/features/soil.webp', en: ['Soil Type Analysis', 'Classify soil type from images'], ar: ['تحليل نوع التربة', 'تصنيف التربة من الصور'] },
  { id: 'crop', Icon: Wheat, color: '#D97706', img: '/assets/features/crop-rec.webp', en: ['Crop Recommendation', 'Best crop for your soil & climate'], ar: ['توصية المحاصيل', 'أفضل محصول لتربتك ومناخك'] },
  { id: 'yield', Icon: TrendingUp, color: '#10B981', img: '/assets/features/yield.webp', en: ['Yield Estimator', 'Predict harvest output (kg/acre)'], ar: ['توقع الحصاد', 'توقع الإنتاج (كجم/فدان)'] },
  { id: 'palm', Icon: Trees, color: '#14B8A6', img: '/assets/features/palm-count.webp', en: ['Aerial Palm Counting', 'Count palms from aerial images'], ar: ['عد النخيل الجوي', 'عد النخيل من الصور الجوية'] },
  { id: 'report', Icon: FileText, color: '#6366F1', img: '/assets/features/reporting.webp', en: ['Reporting System', 'Export comprehensive PDF reports'], ar: ['نظام التقارير', 'تصدير تقارير PDF شاملة'] },
];

/* ─── Floating Label Input ─────────────────────────────────────────────────── */
const FloatingInput = ({ id, type = 'text', label, value, onChange, required, maxLength }) => (
  <div className="floating-input-wrapper w-full">
    <input
      id={id} type={type} value={value} onChange={onChange}
      required={required} maxLength={maxLength} placeholder=" "
      className="w-full bg-white/60 dark:bg-[#0F1829]/80 border border-[#1B263B]/15 dark:border-white/10 rounded-xl px-3.5 text-[#1B263B] dark:text-white text-sm outline-none transition-all duration-200 focus:border-[#C47A2C] dark:focus:border-[#C47A2C] focus:shadow-[0_0_0_3px_rgba(196,122,44,0.12)]"
    />
    <label htmlFor={id}>{label}</label>
    <span className="focus-bar" />
  </div>
);

/* ─── Cross-Fade Image Slider ──────────────────────────────────────────────── */
function CrossFadeSlider({ features, activeIdx }) {
  return (
    <div className="absolute inset-0">
      <AnimatePresence initial={false}>
        <motion.img
          key={activeIdx}
          src={features[activeIdx].img}
          alt={features[activeIdx].en[0]}
          loading="lazy"
          initial={{ opacity: 0, scale: 1.04 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.4, ease: 'easeInOut' }}
          className="absolute inset-0 w-full h-full object-cover"
          onError={e => { e.target.style.opacity = 0; }}
        />
      </AnimatePresence>
    </div>
  );
}

/* ─── Showcase Panel ───────────────────────────────────────────────────────── */
function ShowcasePanel({ features, lang, activeIdx, setActiveIdx }) {
  const feature = features[activeIdx];
  const FIcon = feature.Icon;

  return (
    <div className="relative w-full h-full overflow-hidden bg-[#1B263B]">
      <CrossFadeSlider features={features} activeIdx={activeIdx} />

      {/* Dark gradient overlays for readability */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#050C18]/95 via-[#050C18]/30 to-[#050C18]/50 pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#050C18]/40 to-transparent pointer-events-none" />

      {/* Grid texture overlay */}
      <div className="absolute inset-0 opacity-[0.035] pointer-events-none"
        style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,1) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,1) 1px,transparent 1px)', backgroundSize: '48px 48px' }} />

      {/* Top: Logo + subtitle */}
      <div className="absolute top-0 left-0 right-0 p-10 z-10">
        <motion.div initial={{ opacity: 0, y: -18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}>
          <img src={GeoLogo} alt="GeoNutria" className="h-16 object-contain drop-shadow-2xl" />
        </motion.div>
        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
          className="mt-3 text-white/50 text-sm font-medium tracking-wide"
        >
          {lang === 'ar' ? 'الزراعة الذكية المدعومة بالذكاء الاصطناعي' : 'AI-Powered Smart Agriculture Platform'}
        </motion.p>
      </div>

      {/* Center badge */}
      <motion.div
        initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.2, delay: 0.6 }}
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 text-center pointer-events-none select-none"
      >
        <p className="text-[9rem] font-black text-white/[0.07] leading-none">11</p>
        <p className="text-white/20 text-xs tracking-[0.3em] uppercase -mt-4">
          {lang === 'ar' ? 'خدمة ذكاء اصطناعي' : 'AI Services'}
        </p>
      </motion.div>

      {/* Bottom: active feature chip + dot nav */}
      <div className="absolute bottom-0 left-0 right-0 px-10 pb-10 z-10">
        <AnimatePresence mode="wait">
          <motion.div
            key={feature.id}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="mb-5"
          >
            <div
              className="inline-flex items-center gap-3.5 px-5 py-3 rounded-2xl backdrop-blur-sm"
              style={{ background: `${feature.color}1E`, border: `1px solid ${feature.color}40` }}
            >
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: `${feature.color}28` }}>
                <FIcon size={20} style={{ color: feature.color }} />
              </div>
              <div>
                <p className="text-white font-bold text-base leading-snug">
                  {lang === 'ar' ? feature.ar[0] : feature.en[0]}
                </p>
                <p className="text-white/50 text-xs mt-0.5">
                  {lang === 'ar' ? feature.ar[1] : feature.en[1]}
                </p>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Icon strip — all 11 features */}
        <div className="flex flex-wrap gap-2 mb-5">
          {features.map((f, i) => {
            const FI = f.Icon;
            return (
              <button
                key={f.id}
                onClick={() => setActiveIdx(i)}
                title={lang === 'ar' ? f.ar[0] : f.en[0]}
                className="transition-all duration-300 rounded-xl p-2 backdrop-blur-sm"
                style={{
                  background: i === activeIdx ? `${f.color}35` : 'rgba(255,255,255,0.06)',
                  border: `1px solid ${i === activeIdx ? f.color + '70' : 'rgba(255,255,255,0.10)'}`,
                  transform: i === activeIdx ? 'translateY(-2px)' : 'none',
                }}
              >
                <FI size={15} style={{ color: i === activeIdx ? f.color : 'rgba(255,255,255,0.45)' }} />
              </button>
            );
          })}
        </div>

        {/* Progress dots */}
        <div className="flex gap-1.5">
          {features.map((f, i) => (
            <button
              key={f.id}
              onClick={() => setActiveIdx(i)}
              className="transition-all duration-500 rounded-full"
              style={{
                width: i === activeIdx ? '24px' : '6px',
                height: '6px',
                background: i === activeIdx ? feature.color : 'rgba(255,255,255,0.25)',
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── Auth Panel ───────────────────────────────────────────────────────────── */
function AuthPanel({ theme, setTheme, lang, setLang, isRTL, t, loggingIn,
  username, setUsername, password, setPassword, handleLogin, loginWithGoogle,
  isVerifyOTP, otpCode, setOtpCode, handleVerifyOTP, regData,
  isRegister, setIsRegister }) {

  return (
    <div className="relative flex flex-col items-center justify-center w-full h-full min-h-screen
      px-8 py-12 bg-[#F4EFEA] dark:bg-[#0A0F1E] transition-colors duration-300">

      {/* Top-right controls */}
      <div className={`absolute top-5 flex items-center gap-2 z-10 ${isRTL ? 'left-5' : 'right-5'}`}>
        <button
          onClick={() => setLang(lang === 'en' ? 'ar' : 'en')}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold
            bg-[#1B263B]/6 dark:bg-white/8 hover:bg-[#C47A2C]/10 dark:hover:bg-[#C47A2C]/15
            text-[#1B263B]/60 dark:text-white/50 hover:text-[#C47A2C] dark:hover:text-[#C47A2C]
            transition-all border border-[#1B263B]/8 dark:border-white/8 hover:border-[#C47A2C]/25"
        >
          <Languages size={13} />
          {lang === 'en' ? 'العربية' : 'English'}
        </button>
        <button
          onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}
          className="p-2.5 rounded-full bg-[#1B263B]/6 dark:bg-white/8
            hover:bg-[#1B263B]/12 dark:hover:bg-white/15 transition-all
            text-[#1B263B] dark:text-white border border-[#1B263B]/8 dark:border-white/8"
        >
          {theme === 'light' ? <Moon size={15} /> : <Sun size={15} />}
        </button>
      </div>

      {/* Glass card */}
      <motion.div
        initial={{ opacity: 0, y: 28 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-sm"
      >
        <div className="login-glass">

          {/* Logo */}
          <div className="flex flex-col items-center mb-7">
            <img src={GeoLogo} alt="GeoNutria" className="h-11 object-contain drop-shadow-md mb-4" />
            <h2 className="text-xl font-bold text-[#1B263B] dark:text-white text-center">
              {isVerifyOTP ? (t.login_ext?.otpTitle || 'Verify Your Account') : t.login_ext.welcomeBack}
            </h2>
            <p className="text-xs text-[#1B263B]/40 dark:text-white/35 mt-1 text-center">
              {t.login_ext.signInSubtitle}
            </p>
          </div>

          <AnimatePresence mode="wait">
            {isVerifyOTP ? (
              /* OTP Form */
              <motion.form key="otp" onSubmit={handleVerifyOTP}
                initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }} className="space-y-5"
              >
                <div className="flex items-center gap-3 bg-[#6B8F71]/10 border border-[#6B8F71]/30 rounded-xl p-3.5">
                  <CheckCircle className="text-[#6B8F71] flex-shrink-0" size={20} />
                  <span className="text-[#6B8F71] text-xs font-semibold">
                    {t.login_ext.otpSentTo} {regData?.email}
                  </span>
                </div>
                <p className="text-xs text-[#1B263B]/40 dark:text-white/35 text-center">{t.login_ext.otpHint}</p>
                <input
                  type="text" required value={otpCode}
                  onChange={e => setOtpCode(e.target.value)} maxLength={6}
                  placeholder="• • • • • •"
                  className="w-full bg-[#F4EFEA] dark:bg-[#0F1829] border border-[#1B263B]/15 dark:border-white/10
                    rounded-xl p-3 text-[#1B263B] dark:text-white focus:border-[#C47A2C]
                    focus:shadow-[0_0_0_3px_rgba(196,122,44,0.12)] outline-none
                    text-center text-2xl tracking-[0.6em] font-mono transition-all"
                />
                <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                  disabled={loggingIn}
                  className="w-full bg-gradient-to-r from-[#C47A2C] to-[#A36322] text-white
                    font-semibold py-3 rounded-xl shadow-[0_4px_14px_rgba(196,122,44,0.4)]
                    transition-all disabled:opacity-60 disabled:cursor-wait text-sm"
                >
                  {loggingIn
                    ? <span className="flex items-center justify-center gap-2"><span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />{t.login_ext.verifying}</span>
                    : t.login_ext.verifyBtn}
                </motion.button>
              </motion.form>
            ) : (
              /* Login Form */
              <motion.form key="login" onSubmit={handleLogin}
                initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }} transition={{ duration: 0.3 }} className="space-y-4"
              >
                <FloatingInput id="login-email" label={lang === 'ar' ? 'البريد الإلكتروني / اسم المستخدم' : 'Email / Username'}
                  value={username} onChange={e => setUsername(e.target.value)} required />
                <FloatingInput id="login-password" type="password" label={lang === 'ar' ? 'كلمة المرور' : 'Password'}
                  value={password} onChange={e => setPassword(e.target.value)} required />

                <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                  disabled={loggingIn}
                  className="w-full bg-gradient-to-r from-[#C47A2C] to-[#A36322] text-white
                    font-semibold py-3 rounded-xl shadow-[0_4px_14px_rgba(196,122,44,0.4)]
                    transition-all disabled:opacity-60 disabled:cursor-wait text-sm mt-1"
                >
                  {loggingIn
                    ? <span className="flex items-center justify-center gap-2"><span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />{t.login.loading}</span>
                    : t.login.btn}
                </motion.button>

                {/* Divider */}
                <div className="flex items-center gap-3 my-1">
                  <div className="flex-1 h-px bg-[#1B263B]/10 dark:bg-white/10" />
                  <span className="text-xs text-[#1B263B]/35 dark:text-white/30">{t.login_ext.orContinueWith}</span>
                  <div className="flex-1 h-px bg-[#1B263B]/10 dark:bg-white/10" />
                </div>

                {/* Google */}
                <motion.button type="button"
                  whileHover={{ scale: 1.01, boxShadow: '0 4px 16px rgba(196,122,44,0.18)' }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => loginWithGoogle()}
                  disabled={loggingIn}
                  className="w-full flex items-center justify-center gap-3 bg-white dark:bg-white/5
                    border border-[#1B263B]/12 dark:border-white/10 hover:border-[#C47A2C]/40
                    text-[#1B263B] dark:text-white font-medium py-2.5 rounded-xl
                    transition-all shadow-sm text-sm disabled:opacity-60"
                >
                  <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                  {t.login_ext.continueGoogle}
                </motion.button>
              </motion.form>
            )}
          </AnimatePresence>

          {/* Footer */}
          <div className="mt-6 flex flex-col items-center gap-2.5">
            {!isVerifyOTP && (
              <button
                onClick={() => setIsRegister(true)}
                className="text-xs text-[#6B8F71] hover:text-[#527056] dark:hover:text-[#8BAF91] font-medium transition-colors"
              >
                {t.login_ext.dontHaveAccount} <span className="underline underline-offset-2">{t.login_ext.signUp}</span>
              </button>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
}

/* ─── Main LoginView ───────────────────────────────────────────────────────── */
export default function LoginView({
  toast, setToast, theme, setTheme, lang, setLang, isRTL, t, loggingIn,
  username, setUsername, password, setPassword, handleLogin, loginWithGoogle,
  isVerifyOTP, otpCode, setOtpCode, handleVerifyOTP, regData,
  isRegister, setIsRegister,
  isGoogleOnboarding, googleUserDetails, handleRegisterSubmit, setIsGoogleOnboarding,
}) {
  const [activeIdx, setActiveIdx] = useState(0);

  /* Auto-advance every 4.5s */
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveIdx(p => (p + 1) % SHOWCASE_FEATURES.length);
    }, 4500);
    return () => clearInterval(timer);
  }, []);

  const showOnboarding = isGoogleOnboarding || isRegister;

  return (
    <div
      className="min-h-screen flex overflow-hidden bg-[#F4EFEA] dark:bg-[#0A0F1E] transition-colors duration-300"
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <Toast message={toast.message} type={toast.type} onClose={() => setToast({ message: '', type: 'success' })} />

      {/* ── Showcase Panel (left 58%, desktop only, hidden during onboarding) ── */}
      {!showOnboarding && (
        <div className="hidden lg:block" style={{ width: '58%', flexShrink: 0, height: '100vh', position: 'sticky', top: 0 }}>
          <ShowcasePanel
            features={SHOWCASE_FEATURES}
            lang={lang}
            activeIdx={activeIdx}
            setActiveIdx={setActiveIdx}
          />
        </div>
      )}

      {/* ── Auth / Onboarding Panel (right 42%) ── */}
      <div className={`flex-1 ${showOnboarding ? 'flex items-center justify-center' : ''} overflow-y-auto`}
        style={{ minHeight: '100vh' }}>
        {isGoogleOnboarding ? (
          <div className="w-full max-w-xl px-6 py-10">
            <OnboardingWizard
              isGoogleMode={true}
              initialData={{
                first_name: googleUserDetails?.given_name || '',
                last_name: googleUserDetails?.family_name || '',
                email: googleUserDetails?.email || '',
              }}
              onSubmit={handleRegisterSubmit}
              onCancel={() => setIsGoogleOnboarding(false)}
            />
          </div>
        ) : isRegister ? (
          <div className="w-full max-w-xl px-6 py-10">
            <OnboardingWizard
              isGoogleMode={false}
              onSubmit={handleRegisterSubmit}
              onCancel={() => setIsRegister(false)}
            />
          </div>
        ) : (
          <AuthPanel
            theme={theme} setTheme={setTheme}
            lang={lang} setLang={setLang} isRTL={isRTL} t={t}
            loggingIn={loggingIn}
            username={username} setUsername={setUsername}
            password={password} setPassword={setPassword}
            handleLogin={handleLogin} loginWithGoogle={loginWithGoogle}
            isVerifyOTP={isVerifyOTP} otpCode={otpCode} setOtpCode={setOtpCode}
            handleVerifyOTP={handleVerifyOTP} regData={regData}
            isRegister={isRegister} setIsRegister={setIsRegister}
          />
        )}
      </div>
    </div>
  );
}
